import * as _ from 'lodash';
import { Mission } from '../objects/Mission';
import { CalendarPosition } from '../misc/calendarPosition';

function compareMissionSameOrderNumber(a, b) {
    if (a.End == null && b.End == null) {
        return a.Start.getTime() - b.Start.getTime();
    }
    if (a.End == null) return 1;
    if (b.End == null) return -1;
    return a.End.getTime() - b.End.getTime();
}

function compareMission(a, b) {
    if (a.OrderNumber === b.OrderNumber) {
        return compareMissionSameOrderNumber(a, b);
    }
    if (a.OrderNumber === 0) return 1;
    if (b.OrderNumber === 0) return -1;
    return a.OrderNumber - b.OrderNumber;
}

function isOverlapping(a, b) {
    return a.Start <= b.End && b.Start <= a.End;
}

function isOverlappingLenient(a, b) {
    return a.Start < b.End && b.Start < a.End;
}

/**
 * 
 * @param {Mission[]} missions
 */
function getOverlapping(missions, allowSameDay) {
    let overlapping = [];
    let candidates = _.filter(missions, x => x.OrderNumber > 0 && (x.calendarPosition === CalendarPosition.Active || x.calendarPosition === CalendarPosition.Future));
    let groups = _.groupBy(candidates, x => x.OrderNumber);
    let test = allowSameDay ? isOverlappingLenient : isOverlapping;

    _.each(groups, group => {
        for (let ii = 0; ii < group.length; ++ii) {
            for (let kk = ii + 1; kk < group.length; ++kk) {
                if (test(group[ii], group[kk])) {
                    overlapping.push(group[ii]);
                    overlapping.push(group[kk]);
                }
            }
        }
    });

    return _.uniq(overlapping);
}

/**
 *
 * @param {Mission[]} missions
 */
function getDuplicates(missions) {
    let candidates = _.filter(missions, x => x.PersonId != null);
    var groups = _.groupBy(candidates, x => x.PersonId);
    var multi = _.filter(groups, x => x.length > 1);
    return _.flatMap(multi);
}

/**
 * Generate mission list with placeholder.
 * A single placeholder is created for every order number that does not have an active mission.
 * If the mandateperiod has a substitute/member count, generate placeholders for those ordernumbers
 * as well.
 *
 * @param {MandatePeriod} mandatePeriod
 * @param {Mission[]} missions
 */
function generateMissions(mandatePeriod, missions) {

    const subCount = _.isInteger(mandatePeriod.SubstituteCount) ? mandatePeriod.SubstituteCount : 0;
    const memCount = _.isInteger(mandatePeriod.MemberCount) ? mandatePeriod.MemberCount : 0;
    const maxActiveOrderNumber = missions.reduce((p, v) => v.calendarPosition === CalendarPosition.Historic ? p : Math.max(p, v.OrderNumber), 0);
    const targetPlaceholderOrder = Math.max(subCount + memCount + 1, maxActiveOrderNumber);

    let result = [];
    let zeros = [];
    let nextOrderNumber = 1;

    // Missions are sorted with ascending order number (except for ordernumber 0 which is sorted last).
    missions.sort(compareMission);

    function generatePlaceholders(upto) {
        while (nextOrderNumber < upto) {
            result.push(Mission.createPlaceholder(nextOrderNumber));
            nextOrderNumber = nextOrderNumber + 1;
        }
    }

    for (var ms of missions) {
        if (ms.OrderNumber > 0) {
            if (ms.calendarPosition === CalendarPosition.Active || ms.calendarPosition === CalendarPosition.Future) {
                generatePlaceholders(ms.OrderNumber);
                nextOrderNumber = ms.OrderNumber + 1;
                result.push(ms);
            } else if (ms.calendarPosition === CalendarPosition.Historic) {
                generatePlaceholders(Math.min(ms.OrderNumber, targetPlaceholderOrder));
                result.push(ms);
            }
        } else {
            zeros.push(ms);
        }
    }

    generatePlaceholders(targetPlaceholderOrder);

    return result.concat(zeros);
}

export class OrganizationMissionList {
    /**
     * 
     * @param {MandatePeriod} mandatePeriod
     * @param {Mission[]} missions
     */
    constructor(mandatePeriod, missions) {
        this._actualMissions = missions;

        if (mandatePeriod.calendarPosition === CalendarPosition.Historic) {
            missions.sort(compareMission);
            this._missions = missions;
        } else {
            this._missions = generateMissions(mandatePeriod, missions);
        }

        this.overlapping = getOverlapping(missions, mandatePeriod.AllowSameDayOverlap);
        this.hasOverlappingOrderNumber = this.overlapping.length > 0;

        this.duplicates = getDuplicates(missions);
        this.hasDuplicatePersons = this.duplicates.length > 0;

        this.hasNonHistoric = _.some(this._missions,
            x => x.isPlaceholder() ||
            x.calendarPosition === CalendarPosition.Active ||
            x.calendarPosition === CalendarPosition.Future);

        this._cache = null;
        this._cacheHistoric = false;
        this._cacheFuture = false;
    }

    getActualMissions() {
        return this._actualMissions;
    }

    /**
     * Returns the missions in this list, bypassing the cache.
     * @param {boolean} includeHistoric
     * @param {boolean} includeFuture
     */
    findMissions(includeHistoric, includeFuture) {
        if (includeHistoric && includeFuture) {
            return this._missions;
        }

        return _.filter(this._missions, x =>
            x.isPlaceholder() ||
            x.calendarPosition === CalendarPosition.Active ||
            (includeFuture && x.calendarPosition === CalendarPosition.Future) ||
            (includeHistoric && x.calendarPosition === CalendarPosition.Historic));
    }

    /**
     * Returns the missions in this list, using a cached result if possible.
     * @param {boolean} includeHistoric
     * @param {boolean} includeFuture
     */
    getMissions(includeHistoric, includeFuture) {
        if (includeHistoric && includeFuture) {
            return this._missions;
        }

        if (this._cache == null || this._cacheHistoric !== includeHistoric || this._cacheFuture !== includeFuture) {
            this._cache = this.findMissions(includeHistoric, includeFuture);
            this._cacheHistoric = includeHistoric;
            this._cacheFuture = includeFuture;
        }

        return this._cache;
    }
}